import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api, qs, useAction, useApi } from '../lib/api';
import { money, pct, period } from '../lib/format';
import { useAccounts, useSubsidiaries } from '../lib/lookups';
import { useSession } from '../lib/session';
import type { AccrualSuggestion, Checklist, CloseOverview, CloseTickResult, Flux } from '../lib/types';
import { Icons } from '../components/icons';
import { Badge, Button, Card, cx, Empty, Field, Modal, PageHeader, Progress, Query, Segmented, Select, StatusBadge, Table, Td, Textarea, Th } from '../components/ui';

export function ClosePage() {
  const [params, setParams] = useSearchParams();
  const { can } = useSession();
  const subs = useSubsidiaries();
  const overview = useApi<CloseOverview>('/api/close/overview');
  const selected = params.get('period') ?? overview.data?.nextToClose ?? overview.data?.currentPeriod ?? null;
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reason, setReason] = useState('');

  const run = useAction(() => api<CloseTickResult>('/api/close/run', { body: {} }), {
    success: (r) => `Close tick: ${r.invoicesIssued} invoices issued, ${r.revenueLinesPosted} revenue and ${r.amortizationLinesPosted} amortization lines posted, ${r.reversalsPosted} reversals${r.contractsCompleted ? `, ${r.contractsCompleted} contracts completed` : ''}`,
  });
  const close = useAction((p: string) => api(`/api/periods/${p}/close`, { body: {} }), { success: 'Period closed and locked' });
  const reopen = useAction((p: string) => api(`/api/periods/${p}/reopen`, { body: { reason } }), { success: 'Period reopened (audited)' });

  const periods = [...new Set((overview.data?.periods ?? []).map((p) => p.period))].sort().reverse();
  const statusOf = (p: string) => {
    const rows = (overview.data?.periods ?? []).filter((x) => x.period === p);
    const closed = rows.filter((r) => r.status === 'CLOSED').length;
    return closed === 0 ? 'OPEN' : closed === rows.length ? 'CLOSED' : 'PARTIAL';
  };
  const selStatus = selected ? statusOf(selected) : 'OPEN';

  return (
    <>
      <PageHeader
        title="Close"
        description="Continuous close: revenue, amortization and reversals post as their dates arrive, so month-end is a review. Auto-checks re-run every time you open this page."
        actions={
          can('ACCOUNTANT') && (
            <Button icon={<Icons.Bolt size={15} />} loading={run.isPending} onClick={() => run.mutate()}>
              Run close tick now
            </Button>
          )
        }
      />
      {overview.data && (
        <div className="mb-5 flex gap-1.5 overflow-x-auto pb-1 scrollbar-thin">
          {periods.slice(0, 15).map((p) => {
            const st = statusOf(p);
            return (
              <button
                key={p}
                onClick={() => setParams({ period: p })}
                className={cx(
                  'flex h-14 min-w-[92px] shrink-0 flex-col items-start justify-center rounded-lg border px-3 text-left transition-colors',
                  p === selected ? 'border-brand bg-brand-soft' : 'border-line bg-surface hover:border-line-strong',
                )}
              >
                <span className="text-[12.5px] font-medium">{period(p, true)}</span>
                <span className={cx('flex items-center gap-1 text-[11px]', st === 'CLOSED' ? 'text-muted' : st === 'PARTIAL' ? 'text-warn' : 'text-info')}>
                  {st === 'CLOSED' ? <Icons.Lock size={11} /> : <Icons.Unlock size={11} />}
                  {p === overview.data!.currentPeriod ? 'Live' : st === 'CLOSED' ? 'Closed' : st === 'PARTIAL' ? 'Partly closed' : 'Open'}
                </span>
              </button>
            );
          })}
        </div>
      )}
      {selected && (
        <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_400px]">
          <div className="space-y-5">
            <ChecklistCard p={selected} />
            <FluxCard p={selected} />
          </div>
          <div className="space-y-5">
            <Card title={`${period(selected)} status`}>
              <div className="space-y-2">
                {(overview.data?.periods ?? [])
                  .filter((x) => x.period === selected)
                  .map((x) => (
                    <div key={x.subsidiaryId} className="flex items-center justify-between text-[12.5px]">
                      <span>{subs.byId.get(x.subsidiaryId)?.name}</span>
                      <StatusBadge status={x.status} />
                    </div>
                  ))}
              </div>
              {can('ADMIN') && (
                <div className="mt-4 flex gap-2">
                  {selStatus !== 'CLOSED' ? (
                    <Button variant="primary" className="flex-1" icon={<Icons.Lock size={15} />} loading={close.isPending} onClick={() => close.mutate(selected)}>
                      Close {period(selected, true)}
                    </Button>
                  ) : (
                    <Button className="flex-1" icon={<Icons.Unlock size={15} />} onClick={() => setReopenOpen(true)}>
                      Reopen {period(selected, true)}
                    </Button>
                  )}
                </div>
              )}
              <p className="mt-3 text-[11.5px] text-muted">Closing locks the period: nothing can post into it, including the scheduler, which books late items as catch-ups in the next open period.</p>
            </Card>
            <AccrualsCard p={selected} closed={selStatus === 'CLOSED'} />
          </div>
        </div>
      )}
      <Modal
        open={reopenOpen}
        onClose={() => setReopenOpen(false)}
        title={`Reopen ${period(selected)}`}
        footer={
          <>
            <Button onClick={() => setReopenOpen(false)}>Cancel</Button>
            <Button variant="danger" disabled={reason.trim().length < 5} loading={reopen.isPending} onClick={() => reopen.mutate(selected!, { onSuccess: () => setReopenOpen(false) })}>
              Reopen period
            </Button>
          </>
        }
      >
        <Field label="Reason" hint="Required. Reopening is recorded in the audit log and flagged to controls.">
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Late vendor invoice for September consulting" />
        </Field>
      </Modal>
    </>
  );
}

function ChecklistCard({ p }: { p: string }) {
  const q = useApi<Checklist>(`/api/close/${p}/checklist`);
  const { can } = useSession();
  const update = useAction((x: { id: string; status: string }) => api(`/api/close/tasks/${x.id}`, { method: 'PATCH', body: { status: x.status } }), { silent: false });
  return (
    <Card
      title="Close checklist"
      subtitle={q.data ? `${q.data.done} of ${q.data.total} done · auto-checks verify against the ledger` : undefined}
      actions={q.data && <span className="num text-[13px] font-semibold">{pct(q.data.progress, 0)}</span>}
      pad={false}
    >
      <Query q={q}>
        {(c) => {
          const cats = [...new Set(c.tasks.map((t) => t.category))];
          return (
            <>
              <div className="px-4 pt-3 pb-1">
                <Progress value={c.progress} tone={c.progress === 1 ? 'good' : 'brand'} />
              </div>
              {cats.map((cat) => (
                <div key={cat}>
                  <div className="px-4 pt-3 pb-1 text-[11px] font-semibold tracking-wide text-muted uppercase">{cat}</div>
                  <ul>
                    {c.tasks
                      .filter((t) => t.category === cat)
                      .map((t) => {
                        const check = (t as typeof t & { check?: { pass: boolean; detail: string } | null }).check;
                        const done = t.status === 'DONE';
                        return (
                          <li key={t.id} className="flex items-start gap-3 border-b border-line px-4 py-2.5 last:border-b-0">
                            <button
                              disabled={!can('ACCOUNTANT')}
                              onClick={() => update.mutate({ id: t.id, status: done ? 'TODO' : 'DONE' })}
                              className={cx('mt-0.5 flex size-[18px] shrink-0 items-center justify-center rounded border transition-colors', done ? 'border-good bg-good text-white' : 'border-line-strong hover:border-brand')}
                              aria-label={done ? 'Mark not done' : 'Mark done'}
                            >
                              {done && <Icons.Check size={13} />}
                            </button>
                            <div className="min-w-0 flex-1">
                              <div className={cx('text-[13px] font-medium', done && 'text-muted line-through decoration-faint')}>{t.title}</div>
                              <div className="text-[12px] text-muted">{t.description}</div>
                            </div>
                            {check && (
                              <span className={cx('flex max-w-[45%] shrink-0 items-center gap-1.5 text-right text-[11.5px]', check.pass ? 'text-good' : 'text-warn')} title={check.detail}>
                                {check.pass ? <Icons.CheckCircle size={14} /> : <Icons.Alert size={14} />}
                                <span className="truncate">{check.detail}</span>
                              </span>
                            )}
                            {!check && <Badge>Manual</Badge>}
                          </li>
                        );
                      })}
                  </ul>
                </div>
              ))}
            </>
          );
        }}
      </Query>
    </Card>
  );
}

function FluxCard({ p }: { p: string }) {
  const [compare, setCompare] = useState<'prior_period' | 'prior_year'>('prior_period');
  const [onlyFlagged, setOnlyFlagged] = useState(true);
  const [open, setOpen] = useState<string | null>(null);
  const q = useApi<Flux>(`/api/close/${p}/flux${qs({ compare })}`);
  return (
    <Card
      title="Flux analysis"
      subtitle={q.data ? `${period(q.data.period)} vs ${period(q.data.comparePeriod)} · materiality ${money(q.data.materiality, q.data.currency, { cents: false })}` : undefined}
      actions={
        <>
          <label className="flex items-center gap-1.5 text-[12px] text-muted">
            <input type="checkbox" checked={onlyFlagged} onChange={(e) => setOnlyFlagged(e.target.checked)} /> Material only
          </label>
          <Segmented
            value={compare}
            onChange={setCompare}
            items={[
              { key: 'prior_period', label: 'MoM' },
              { key: 'prior_year', label: 'YoY' },
            ]}
          />
        </>
      }
      pad={false}
    >
      <Query q={q}>
        {(f) => {
          const rows = f.rows.filter((r) => !onlyFlagged || r.flagged);
          return rows.length === 0 ? (
            <Empty title="No material variances">Every account moved less than the materiality threshold.</Empty>
          ) : (
            <Table dense>
              <thead>
                <tr>
                  <Th>Account</Th>
                  <Th right>{period(f.period, true)}</Th>
                  <Th right>{period(f.comparePeriod, true)}</Th>
                  <Th right>Variance</Th>
                  <Th right w="80px">
                    %
                  </Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <FluxRowView key={r.accountId} r={r} currency={f.currency} open={open === r.accountId} onToggle={() => setOpen(open === r.accountId ? null : r.accountId)} />
                ))}
              </tbody>
            </Table>
          );
        }}
      </Query>
    </Card>
  );
}

function FluxRowView({ r, currency, open, onToggle }: { r: Flux['rows'][number]; currency: string; open: boolean; onToggle: () => void }) {
  return (
    <>
      <tr onClick={onToggle} className="cursor-pointer hover:bg-paper">
        <Td>
          <div className="flex items-center gap-2">
            <span className={cx('text-faint transition-transform', open && 'rotate-90')}>
              <Icons.Chevron size={13} />
            </span>
            <span className="num text-muted">{r.code}</span> {r.name}
            {r.flagged && <span className="size-1.5 rounded-full bg-warn" title="Above materiality" />}
          </div>
        </Td>
        <Td right>{money(r.current, currency, { cents: false })}</Td>
        <Td right muted>
          {money(r.comparison, currency, { cents: false })}
        </Td>
        <Td right className={tone(r.type, r.variance)}>
          {money(r.variance, currency, { cents: false, sign: true })}
        </Td>
        <Td right muted>
          {r.variancePct === null ? 'new' : pct(r.variancePct, 0)}
        </Td>
      </tr>
      {open && (
        <tr>
          <Td colSpan={5} className="bg-paper/70">
            <div className="py-1 pl-6">
              <div className="mb-2 flex items-start gap-2 text-[12.5px]">
                <span className="mt-0.5 text-ai">
                  <Icons.Sparkle size={14} />
                </span>
                <span>{r.explanation}</span>
              </div>
              {r.drivers.length > 0 && (
                <table className="w-full max-w-xl text-[12px]">
                  <tbody>
                    {r.drivers.map((d) => (
                      <tr key={d.name}>
                        <td className="py-0.5 text-muted">{d.name}</td>
                        <td className="num py-0.5 text-right">{money(d.current, currency, { cents: false })}</td>
                        <td className="num py-0.5 text-right text-muted">{money(d.comparison, currency, { cents: false })}</td>
                        <td className={cx('num py-0.5 text-right', tone(r.type, d.variance))}>{money(d.variance, currency, { cents: false, sign: true })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </Td>
        </tr>
      )}
    </>
  );
}

function AccrualsCard({ p, closed }: { p: string; closed: boolean }) {
  const q = useApi<AccrualSuggestion[]>(`/api/close/${p}/accruals`);
  const { can } = useSession();
  const { byId } = useAccounts();
  const [accountFor, setAccountFor] = useState<Record<string, string>>({});
  const book = useAction((s: AccrualSuggestion) => api(`/api/close/${p}/accruals`, { body: { vendorId: s.vendorId, amount: s.amount, accountId: accountFor[s.vendorId] || s.accountId } }), {
    success: 'Accrual booked; it reverses on the 1st of next month',
  });
  return (
    <Card title="Suggested accruals" subtitle="Recurring vendors with no bill yet this period" pad={false}>
      <Query q={q}>
        {(rows) =>
          rows.length === 0 ? (
            <Empty title="No missing bills">Every recurring vendor has billed for {period(p)}.</Empty>
          ) : (
            <ul className="divide-y divide-line">
              {rows.map((s) => (
                <li key={s.vendorId} className="px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <Link to={`/ap/vendors/${s.vendorId}`} className="text-[13px] font-medium hover:text-brand hover:underline">
                        {s.vendorName}
                      </Link>
                      <div className="text-[12px] text-muted">{s.reason}</div>
                    </div>
                    <div className="text-right">
                      <div className="num text-[13.5px] font-semibold">{money(s.amount, s.currency)}</div>
                      <div className="text-[11.5px] text-muted">{pct(s.confidence, 0)} confidence</div>
                    </div>
                  </div>
                  <div className="mt-2 text-[11.5px] text-muted">
                    Based on {s.basis.map((b) => `${b.number} (${money(b.total, s.currency, { cents: false })})`).join(', ')}
                  </div>
                  {can('ACCOUNTANT') && !closed && (
                    <div className="mt-2.5 flex gap-2">
                      <Select className="h-7 flex-1 text-[12px]" value={accountFor[s.vendorId] ?? s.accountId} onChange={(e) => setAccountFor({ ...accountFor, [s.vendorId]: e.target.value })}>
                        <option value={s.accountId}>{byId.get(s.accountId)?.name ?? 'Expense account'}</option>
                        {[...byId.values()]
                          .filter((a) => a.type === 'EXPENSE' && a.id !== s.accountId)
                          .map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.code} · {a.name}
                            </option>
                          ))}
                      </Select>
                      <Button size="sm" variant="primary" loading={book.isPending && book.variables?.vendorId === s.vendorId} onClick={() => book.mutate(s)}>
                        Book accrual
                      </Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )
        }
      </Query>
    </Card>
  );
}

/** Revenue up is favourable, expense up is not; balance-sheet movements carry no judgement. */
function tone(type: string, variance: number) {
  if (!variance) return '';
  const sign = ['INCOME', 'REVENUE', 'OTHER_INCOME'].includes(type) ? 1 : ['EXPENSE', 'COST_OF_REVENUE', 'OPERATING_EXPENSE', 'OTHER_EXPENSE', 'DEPRECIATION', 'INCOME_TAX'].includes(type) ? -1 : 0;
  return sign * variance > 0 ? 'text-good' : sign * variance < 0 ? 'text-bad' : '';
}
